import { Heading, SegmentedTabs } from "@overmap-ai/blocks"
import { type IssueTypeFields, type Stored, EMPTY_ARRAY } from "@overmap-ai/common"
import {
	selectAttachmentsOfIssueTypeFields,
	selectIssueTypeById,
	selectIssueTypeFieldsOfIssueType,
	selectLatestIssueTypeFieldsOfIssueType,
} from "@overmap-ai/core"
import { FieldSchema, type FieldSchemaConfig, FormBuilder, getImagesFromFields } from "@overmap-ai/forms"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { FieldsRevisionsTimeline } from "../../components/FieldsRevisionsTimeline/FieldsRevisionsTimeline"
import { useDeserializeFieldSchemaConfig, useHydrateFieldsWithAttachments } from "../../components/Form/hooks"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/issue-types/$issueTypeId/fields")

export const IssueTypeSettingsFieldsView = memo(() => {
	const { issueTypeId } = routeApi.useParams()
	const issueType = useAppSelector(selectIssueTypeById(issueTypeId))

	if (!issueType) {
		throw new Error(`expected issueType to exist, got ${issueType}.`)
	}

	const latestIssueTypeFields = useAppSelector(selectLatestIssueTypeFieldsOfIssueType(issueType.uuid))
	const issueTypeFieldsRevisions = useShallowEqaulSelector(selectIssueTypeFieldsOfIssueType(issueType.uuid))
	const attachments = useShallowEqaulSelector(selectAttachmentsOfIssueTypeFields(latestIssueTypeFields?.uuid ?? ""))
	const navigate = useNavigate()

	const handleIssueTypeFieldsSelect = useCallback(
		(issueTypeFields: Stored<IssueTypeFields>) => {
			void navigate({
				from: "/$organizationId/settings/issue-types/$issueTypeId/fields",
				to: "/$organizationId/settings/issue-types/$issueTypeId/revisions/$issueTypeFieldsId",
				params: (previousParams) => ({ ...previousParams, issueTypeFieldsId: issueTypeFields.uuid }),
			})
		},
		[navigate],
	)

	const handleFormBuilderSave = useCallback(
		async (fields: FieldSchemaConfig) => {
			const images = await getImagesFromFields(fields)

			const [offlineIssueTypeFields, _promise] = sdk.issueTypeFields.add({
				issue_type: issueType.uuid,
				fields: FieldSchema.serializeConfig(fields),
			})

			if (Object.keys(images).length !== 0) {
				void sdk.issueTypeFieldsAttachments.bulkAdd(
					Object.entries(images).map(([fieldId, file]) => ({
						fields_revision: offlineIssueTypeFields.uuid,
						field_identifier: fieldId,
						file: file,
					})),
				)
			}
		},
		[issueType.uuid],
	)

	const fieldSchemaConfig = useDeserializeFieldSchemaConfig(latestIssueTypeFields?.fields ?? EMPTY_ARRAY)

	const fieldsWithImages = useHydrateFieldsWithAttachments(
		fieldSchemaConfig,
		attachments,
		sdk.issueTypeFieldsAttachments,
	)

	return (
		<ViewContainer>
			<section className="flex size-full flex-col gap-4">
				<Heading size="xl">Fields</Heading>

				<FormBuilder.Root initialFields={fieldsWithImages} onSave={handleFormBuilderSave}>
					<SegmentedTabs.Root defaultValue="edit" className="flex size-full flex-col gap-4 overflow-hidden">
						<SegmentedTabs.List className="flex" accentColor="base" size="sm" variant="surface">
							<SegmentedTabs.Trigger className="grow basis-1" value="edit">
								List
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="flow">
								Flow
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="preview">
								Preview
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="history">
								History
							</SegmentedTabs.Trigger>
						</SegmentedTabs.List>

						<SegmentedTabs.Content value="edit" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.ListBuilder />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="flow" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.FlowBuilder />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="preview" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.Preview />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="history" className="size-full overflow-y-auto overflow-x-clip">
							<FieldsRevisionsTimeline
								fieldsRevisions={issueTypeFieldsRevisions}
								onRevisionSelect={handleIssueTypeFieldsSelect}
							/>
						</SegmentedTabs.Content>
					</SegmentedTabs.Root>
				</FormBuilder.Root>
			</section>
		</ViewContainer>
	)
})
IssueTypeSettingsFieldsView.displayName = "IssueTypeSettingsFieldsView"
